import { Menu, MenuItem, Tooltip } from '@mui/material';
import { FC, useState } from 'react';
import { Download, Minus, Plus } from 'react-feather';
import styled from 'styled-components';
import { isDevelopment } from '../../../../configs';
import { useEditorData, useFabricEditor } from '../../lib/FabricJs';
import { isSnapEnabled, setSnapEnabled } from '../../lib/FabricJs/utils/guides';
import { SvgButton } from '../../../common/ToolBar';
import { editorTheme } from '../../theme';
import {
  getZoomLimits,
  useZoom,
  ZOOM_PRESETS,
} from '../../lib/FabricJs/hooks/useZoom';
import { getControlButtonText } from '../../utils/utils';

const MENU_PRESETS = [0.25, 0.5, 1, 1.5, 2];

const FooterBarContainer = styled.footer`
  background: ${editorTheme.surface};
  display: flex;
  flex: none;
  height: 40px;
  border-top: 1px solid ${editorTheme.border};
  padding: 0 12px;
  align-items: center;
  justify-content: space-between;
`;

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${editorTheme.text};
  cursor: pointer;
  & input {
    accent-color: ${editorTheme.accent};
    margin: 0;
  }
`;

const Settings = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ZoomButton = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: none;
  color: ${editorTheme.text};
  &:hover:not(:disabled) {
    background: ${editorTheme.surfaceMuted};
  }
  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const ZoomValue = styled.button`
  min-width: 64px;
  height: 28px;
  padding: 0 8px;
  border-radius: 6px;
  background: none;
  color: ${editorTheme.text};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  &:hover {
    background: ${editorTheme.surfaceMuted};
  }
`;

const percent = (zoom: number) => `${Math.round(zoom * 100)}%`;

const exportJSonToFile = (json: unknown) => {
  const link = document.createElement('a');
  const file = new Blob([JSON.stringify(json)], { type: 'text/plain' });
  link.href = URL.createObjectURL(file);
  link.download = 'json-file.json';
  link.click();
  URL.revokeObjectURL(link.href);
};

const FooterBar: FC = () => {
  const { canvas } = useFabricEditor();
  const { showRulers, setShowRulers } = useEditorData();
  const [snap, setSnap] = useState(isSnapEnabled);
  const { zoom, isFit, zoomTo, zoomIn, zoomOut, zoomToFit } = useZoom();
  const [zoomMenu, setZoomMenu] = useState<HTMLElement | null>(null);
  const limits = canvas ? getZoomLimits(canvas) : { min: 0, max: 0 };
  const controlButtonText = getControlButtonText();

  const onExportToJson = () => {
    const json = canvas?.toJSON(['data']); // TODO: reuse this
    if (json) {
      exportJSonToFile(json);
    }
  };
  return (
    <FooterBarContainer>
      <Settings>
        <Tooltip title="Snap to the slide and to other objects while dragging">
          <Toggle>
            <input
              type="checkbox"
              checked={snap}
              onChange={(event) => {
                setSnapEnabled(event.target.checked);
                setSnap(event.target.checked);
              }}
            />
            Snap to guides
          </Toggle>
        </Tooltip>
        <Toggle>
          <input
            type="checkbox"
            checked={showRulers}
            onChange={(event) => setShowRulers(event.target.checked)}
          />
          Rulers
        </Toggle>
      </Settings>
      <Group>
        {isDevelopment && (
          <Tooltip title="Download is available only for the development">
            <SvgButton onClick={onExportToJson}>
              <Download size={18} />
            </SvgButton>
          </Tooltip>
        )}
        <Tooltip title={`Zoom out (${controlButtonText} + −)`}>
          <span>
            <ZoomButton
              aria-label="Zoom out"
              onClick={zoomOut}
              disabled={zoom <= ZOOM_PRESETS[0] * 1.01}
            >
              <Minus size={16} />
            </ZoomButton>
          </span>
        </Tooltip>
        <ZoomValue
          aria-label="Zoom level"
          aria-haspopup="menu"
          onClick={(event) => setZoomMenu(event.currentTarget)}
        >
          {percent(zoom)}
          {isFit ? ' · Fit' : ''}
        </ZoomValue>
        <Tooltip title={`Zoom in (${controlButtonText} + +)`}>
          <span>
            <ZoomButton
              aria-label="Zoom in"
              onClick={zoomIn}
              disabled={zoom >= limits.max * 0.99}
            >
              <Plus size={16} />
            </ZoomButton>
          </span>
        </Tooltip>
        <Menu
          anchorEl={zoomMenu}
          open={!!zoomMenu}
          onClose={() => setZoomMenu(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <MenuItem
            selected={isFit}
            onClick={() => {
              zoomToFit();
              setZoomMenu(null);
            }}
          >
            Fit to screen ({controlButtonText} + 0)
          </MenuItem>
          {MENU_PRESETS.filter((preset) => preset <= limits.max).map(
            (preset) => (
              <MenuItem
                key={preset}
                selected={!isFit && Math.abs(zoom - preset) < 0.005}
                onClick={() => {
                  zoomTo(preset);
                  setZoomMenu(null);
                }}
              >
                {percent(preset)}
              </MenuItem>
            ),
          )}
        </Menu>
      </Group>
    </FooterBarContainer>
  );
};

export default FooterBar;
